import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faSpinner, faEye, faTimes, faRefresh, faExternalLinkAlt, faPlus } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError } from '@/utils/toasts';
import { formatCurrencyCentValue } from '@/utils/helpers';
import SubscriptionDetailModal from '@/components/admin/subscriptions/SubscriptionDetailModal';
import CancelSubscriptionModal from '@/components/admin/subscriptions/CancelSubscriptionModal';
import CreateExternalSubscriptionModal from '@/components/admin/subscriptions/CreateExternalSubscriptionModal';
import { type Subscription, type User } from '@/models';
import { useUser } from '@/contexts/UserContext';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';

interface UserSubscriptionsCardProps {
  user: User;
}

function StatusBadge({ status }: { status: Subscription['status'] }) {
  const statusConfig: Record<Subscription['status'], { bg: string; text: string; label: string }> = {
    'ACTIVE': { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-800 dark:text-green-400', label: 'Aktiv' },
    'PENDING_PAYMENT': { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-800 dark:text-yellow-400', label: 'Zahlung ausstehend' },
    'INCOMPLETE': { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-800 dark:text-yellow-400', label: 'Unvollständig' },
    'PAST_DUE': { bg: 'bg-orange-100 dark:bg-orange-900/50', text: 'text-orange-800 dark:text-orange-400', label: 'Überfällig' },
    'CANCELED': { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-400', label: 'Gekündigt' },
    'ABORTED': { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-400', label: 'Checkout abgebrochen' },
    'FAILED': { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-400', label: 'Fehlgeschlagen' }
  };

  const config = statusConfig[status] ?? { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-400', label: status };

  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

export default function UserSubscriptionsCard({ user }: UserSubscriptionsCardProps) {
  const { isAdmin } = useUser();
  const { getTypeLabel } = useSubscriptionProducts();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [cancelingSubscription, setCancelingSubscription] = useState<Subscription | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
  }, [user]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await APIRoutes.getUserSubscriptions(user.id);
      setSubscriptions(response.subscriptions);
    } catch (error) {
      const rawMessage = error instanceof Error ? error.message : String(error);
      const statusMatch = rawMessage.match(/^HTTP\s+(\d{3}):/);
      const statusCode = statusMatch !== null ? Number(statusMatch[1]) : null;
      if (statusCode === 502 || statusCode === 503) {
        setLoadError('Die Abonnements dieses Benutzers konnten gerade nicht geladen werden (Serverfehler 5xx). Bitte spaeter erneut versuchen.');
      } else {
        handleAPIError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCanceled = (updatedSubscription: Subscription) => {
    setSubscriptions(prev =>
      prev.map(sub =>
        sub.id === updatedSubscription.id ? updatedSubscription : sub
      )
    );
    setCancelingSubscription(null);
  };

  const getSubscriptionTypeLabelInt = (subscription: Subscription): string => {
    if (subscription.items.length === 0) {
      return 'Unbekannt';
    }

    return subscription.items.map(p => p.type).join('; ');
  };

  const getSubscriptionTypeLabel = (subscription: Subscription): string => {
    if (subscription.items.length === 0) {
      return 'Unbekannt';
    }

    return subscription.items.map(p => getTypeLabel(p.type)).join('; ');
  };

  const canCancel = (subscription: Subscription): boolean => {
    return subscription.status === 'ACTIVE' || subscription.status === 'PAST_DUE';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <FontAwesomeIcon icon={faCreditCard} className="w-6 h-6 mr-3 text-blue-600 dark:text-blue-400" />
          Abonnements
        </h2>

        <div className="flex gap-4">
          {user.stripeCustomerID !== null && (
            <a
              href={`https://dashboard.stripe.com/acct_1Ms9flL0k1dVagZT/customers/${user.stripeCustomerID}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30 cursor-pointer"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} className="w-4 h-4 mr-2" />
              In Stripe öffnen
            </a>
          )}

          {isAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-900/30 cursor-pointer"
            >
              <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
              Externes Abo hinzufügen
            </button>
          )}

          <button
            onClick={fetchSubscriptions}
            disabled={loading}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer disabled:opacity-50"
            title="Aktualisieren"
          >
            <FontAwesomeIcon icon={faRefresh} className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      ) : loadError !== null ? (
        <div className="text-center py-8">
          <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="text-center py-8">
          <FontAwesomeIcon icon={faCreditCard} className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400 italic">Keine Abonnements vorhanden</p>
        </div>
      ) : (
        <div className="space-y-4">
          {subscriptions.map((subscription) => (
            <div
              key={subscription.id}
              className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2 mb-2">
                    <StatusBadge status={subscription.status} />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {getSubscriptionTypeLabelInt(subscription)}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {getSubscriptionTypeLabel(subscription)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                    <span>
                      <span className="font-medium">{formatCurrencyCentValue(subscription.price, 'EUR')}</span>
                    </span>
                    {subscription.currentPeriodStart !== null && subscription.currentPeriodEnd !== null && (
                      <span>
                        {new Date(subscription.currentPeriodStart).toLocaleDateString('de-DE')} -{' '}
                        {new Date(subscription.currentPeriodEnd).toLocaleDateString('de-DE')}
                      </span>
                    )}
                    {subscription.cancelAtPeriodEnd && (
                      <span className="text-orange-600 dark:text-orange-400 font-medium">
                        Kündigung zum Periodenende
                      </span>
                    )}
                    {subscription.canceledAt !== null && (
                      <span className="text-red-600 dark:text-red-400">
                        Gekündigt: {new Date(subscription.canceledAt).toLocaleDateString('de-DE')}
                      </span>
                    )}
                    {subscription.referrer !== null && subscription.referrerUserID !== null && (
                      <span>
                        Empfehlung durch:{' '}
                        <Link
                          to={`/admin/users/${subscription.referrerUserID}`}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                        >
                          {subscription.referrer.firstName} {subscription.referrer.lastName}
                        </Link>
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 shrink-0">
                  <button
                    onClick={() => setSelectedSubscription(subscription)}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 cursor-pointer flex items-center"
                  >
                    <FontAwesomeIcon icon={faEye} className="w-3.5 h-3.5 mr-1.5" />
                    Details
                  </button>
                  {canCancel(subscription) && !subscription.cancelAtPeriodEnd && (
                    <button
                      onClick={() => setCancelingSubscription(subscription)}
                      className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 cursor-pointer flex items-center"
                    >
                      <FontAwesomeIcon icon={faTimes} className="w-3.5 h-3.5 mr-1.5" />
                      Kündigen
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedSubscription !== null && (
        <SubscriptionDetailModal
          isOpen={selectedSubscription !== null}
          onClose={() => setSelectedSubscription(null)}
          subscription={selectedSubscription}
          userId={user.id}
        />
      )}

      {/* Cancel Modal */}
      {cancelingSubscription !== null && (
        <CancelSubscriptionModal
          isOpen={cancelingSubscription !== null}
          onClose={() => setCancelingSubscription(null)}
          subscription={cancelingSubscription}
          userId={user.id}
          onCanceled={handleCanceled}
        />
      )}

      {/* Create External Subscription Modal */}
      <CreateExternalSubscriptionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        userId={user.id}
        onCreated={fetchSubscriptions}
      />
    </div>
  );
}
